import { DateTime } from "luxon";
import React, { forwardRef, useMemo } from "react";
import { getTimeLineWeekdayNames } from "../core/functions";
import {
  SCROLL_BAR_WIDTH,
  TIMELINE_DEFAULT_HOUR_WIDTH,
  TIMELINE_PADDING,
} from "../core/layoutConstants";
import { TimeLineWeekdayName } from "../core/types";
import { DraggableTimeSelector } from "./DraggableTimeSelector";
import { TimeLines } from "./TimeLine";

interface TimeLineHeaderProps {
  readonly width: number;
  readonly height: number;
  readonly scenarioStart: DateTime;
  readonly scenarioEnd: DateTime;
  readonly zoomLevel: number;
  readonly timeLineDateTimes: DateTime[];
  readonly onDragStart?: () => void;
  readonly onDragEnd?: () => void;
}

function TimeLineHeaderWithRef(
  {
    width,
    height,
    scenarioStart,
    scenarioEnd,
    zoomLevel,
    timeLineDateTimes,
    onDragStart,
    onDragEnd,
  }: TimeLineHeaderProps,
  ref: React.Ref<HTMLDivElement>,
): React.JSX.Element {
  const timeLineWeekdayNames = useMemo<TimeLineWeekdayName[]>(
    () =>
      getTimeLineWeekdayNames(
        {
          year: scenarioStart.year,
          month: scenarioStart.month,
          day: scenarioStart.day,
          hour: scenarioStart.hour,
        },
        { hours: scenarioEnd.diff(scenarioStart).as("hours") },
      ),
    [scenarioStart, scenarioEnd],
  );

  const timeLineWeekdayNamesPositioned = useMemo(() => {
    function applyZoomLevel(value: number) {
      return value * zoomLevel;
    }
    function applyHourWidth(value: number) {
      return value * TIMELINE_DEFAULT_HOUR_WIDTH;
    }
    function applyZoomLevelAndHourWidth(value: number) {
      return applyZoomLevel(applyHourWidth(value));
    }
    return timeLineWeekdayNames.map((dayInfo, index) => {
      const result = {
        ...dayInfo,
        left: applyZoomLevelAndHourWidth(
          timeLineWeekdayNames.reduce(
            (previousValue, currentValue, currentIndex) =>
              currentIndex >= index
                ? previousValue
                : previousValue + currentValue.numberOfHoursInDay,
            0,
          ),
        ),
        width: applyZoomLevelAndHourWidth(dayInfo.numberOfHoursInDay),
      };

      return result;
    });
  }, [timeLineWeekdayNames, zoomLevel]);

  return (
    <div
      className="relative w-full scrollbar-gutter-stable items-start overflow-hidden"
      style={{ height: `${height}px` }}
      ref={ref}
      id="Container"
    >
      <div className="relative col-start-[span_50] block h-6" style={{ width: `${width}px` }}>
        {timeLineWeekdayNamesPositioned.map(({ weekdayName: dayName, width: dayWidth, left }) => {
          return (
            <div
              className="absolute block h-full"
              style={{ left: `${left}px`, width: `${dayWidth}px` }}
              key={`${dayName}-at-${left}`}
            >
              <div
                className="font-weight-normal sticky left-0 inline-flex items-center justify-center overflow-hidden px-[6px] font-default text-m"
                style={{ width: `${TIMELINE_PADDING * 2}px` }}
              >
                {dayName}
              </div>
            </div>
          );
        })}
      </div>

      <DraggableTimeSelector width={width} onDragStart={onDragStart} onDragEnd={onDragEnd} />

      <div style={{ height: `${height}px`, width: `${width + SCROLL_BAR_WIDTH}px` }}>
        <TimeLines
          timeLineDateTimes={timeLineDateTimes}
          scenarioStart={scenarioStart}
          hourWidth={TIMELINE_DEFAULT_HOUR_WIDTH * zoomLevel}
          lineType="header"
        />
      </div>
    </div>
  );
}

export const TimeLineHeader = forwardRef<HTMLDivElement, TimeLineHeaderProps>(
  TimeLineHeaderWithRef,
);
